import { ColumnSettingOptions } from '@flexible-reports/store/slices/types';
import { Classification, ColumnFamily, ComplimentaryFieldMap, SelectedFamily } from '@flexible-reports/store/metadata.types';

/**
 * Builds a set of classifications based on column settings.
 * Need to extend this function to add more classifications.
 * @param columnSettings - The column settings containing currency of view selection
 * @returns Set of classification strings, includes 'CONVERTED' when currency of view is selected
 */
export const buildClassifications = (columnSettings: ColumnSettingOptions) => {
  const isCov = !!columnSettings.selectedCurrencyOfView;
  const classifications = new Set<string>();
  if (isCov) {
    classifications.add('CONVERTED' as Classification);
  }
  return classifications;
};

/**
 * Updates selected fields by adding complimentary fields based on column settings classifications.
 * @returns Updated array of field IDs including complimentary fields that match classifications
 * @param selectedFields
 * @param complimentaryFieldsMap
 * @param columnSettings
 */
export const updateSelectionsByClassifications = (
  selectedFields: string[],
  complimentaryFieldsMap: ComplimentaryFieldMap,
  columnSettings: ColumnSettingOptions
): string[] => {
  const selectedClassifications = buildClassifications(columnSettings);
  return updateSelectedFields(selectedFields, complimentaryFieldsMap, selectedClassifications);
};

/**
 * Updates selected fields by adding complimentary fields that match the selected classifications.
 * @param selectedFields - Array of currently selected field IDs
 * @param complimentaryFieldsMap - Map of fields to their complimentary field data
 * @param selectedClassifications - Set of selected classification strings
 * @returns Array of field IDs including original fields and matching complimentary fields
 */
const updateSelectedFields = (
  selectedFields: string[],
  complimentaryFieldsMap: ComplimentaryFieldMap,
  selectedClassifications: Set<string>
): string[] => {
  const selectedFieldSet = new Set(selectedFields);
  return selectedFields.flatMap(field => {
    const complementaryData = complimentaryFieldsMap[field];
    if (!complementaryData) {
      return [field];
    }

    const validComplimentaryFields = complementaryData.complimentaryFields.filter(compField => {
      if (selectedFieldSet.has(compField)) {
        return false;
      }
      const compClassifications = complimentaryFieldsMap[compField]?.classifications;
      return compClassifications?.every(cls => selectedClassifications.has(cls));
    });

    return [field, ...validComplimentaryFields];
  });
};

/**
 * Filters column families based on selected classifications.
 * @param families - Array of column families to filter
 * @param selectedClassifications - Set of classifications to match against
 * @returns Filtered families containing only matching attributes
 */
export const filterFamilies = (families: ColumnFamily[], selectedClassifications: Set<string>): ColumnFamily[] => {
  // Classifications that require opt-in (hide unless explicitly selected)
  const OPT_IN_CLASSIFICATIONS = ['CONVERTED'];

  return families
    .map(family => {
      if (!family.attributes) {
        const hasOptInClassification = family.classifications?.some(cls => OPT_IN_CLASSIFICATIONS.includes(cls)) ?? false;
        const allClassificationsSelected = family.classifications?.every(cls => selectedClassifications.has(cls)) ?? true;
        return hasOptInClassification && !allClassificationsSelected ? null : family;
      }
      return {
        ...family,
        attributes: family.attributes.filter(attr => {
          const hasOptInClassification = attr.classifications?.some(cls => OPT_IN_CLASSIFICATIONS.includes(cls)) ?? false;
          const allClassificationsSelected = attr.classifications?.every(cls => selectedClassifications.has(cls)) ?? true;
          return !hasOptInClassification || allClassificationsSelected;
        })
      };
    })
    .filter((family): family is ColumnFamily => (!family ? false : !family.attributes || family.attributes.length > 0));
};

/**
 * Filters selected metric families based on classifications.
 * Removes metric families whose classifications don't match the selected classifications.
 * @param selectedFamilies - Array of currently selected metric families
 * @param selectedClassifications - Set of active classification strings
 * @param complimentaryFieldsMap - Map containing classification data for each field
 * @returns Filtered array of metric families that match the selected classifications
 */
export const filterSelectedMetricFamilies = (
  selectedFamilies: SelectedFamily[],
  selectedClassifications: Set<string>,
  complimentaryFieldsMap: ComplimentaryFieldMap
) => {
  return selectedFamilies.filter(selectedFamily => {
    const compClassifications = complimentaryFieldsMap[selectedFamily.id]?.classifications;
    return !compClassifications || compClassifications.every(cls => selectedClassifications.has(cls));
  });
};

/**
 * Filters selected dimension families and their attributes based on classifications.
 * Removes dimension attributes whose classifications don't match, and removes families with no remaining attributes.
 * @param selectedFamilies - Array of currently selected dimension families
 * @param selectedClassifications - Set of active classification strings
 * @param complimentaryFieldsMap - Map containing classification data for each field
 * @returns Filtered array of dimension families with only matching attributes
 */
export const filterSelectedDimensionFamilies = (
  selectedFamilies: SelectedFamily[],
  selectedClassifications: Set<string>,
  complimentaryFieldsMap: ComplimentaryFieldMap
) => {
  return selectedFamilies
    .map(family => ({
      ...family,
      attributes:
        family.attributes &&
        family.attributes.filter(attr => {
          const compClassifications = complimentaryFieldsMap[attr]?.classifications;
          return !compClassifications || compClassifications.every(cls => selectedClassifications.has(cls));
        })
    }))
    .filter(family => !family.attributes || family.attributes.length > 0);
};
